import {
    AiGeneratedLabel,
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    Input,
    Label,
    Text,
} from '@/ui/design-system/components';
import {
    ArrowLeft,
    Check,
    CirclePlus,
    Search,
    Sparkles,
    Trash2,
    X,
} from 'lucide-react';
import {
    CATEGORY_INSTRUCTIONS_PLACEHOLDER,
    CATEGORY_INSTRUCTION_EXAMPLES,
} from './contextConstants';
import {
    ContextManagerCard,
    ContextOverviewSkeleton,
    isContextGenerating,
    isContextPending,
} from './ContextCards';
import {
    useCreateAlertContext,
    useDeleteAlertContext,
    useUpdateAlertContext,
} from '@/utils/hooks/alerts';
import { useEffect, useMemo, useState } from 'react';
import { Textarea } from '@/ui/design-system/components/ui/textarea';
import { cn } from '@/ui/design-system/utils/cn';
import toast from 'react-hot-toast';

const DEFAULT_FORM = {
    name: '',
    context_type: 'brand',
    website_url: '',
    instructions: '',
};

const TYPE_OPTIONS = [
    {
        value: 'brand',
        title: 'Brand',
        description: 'Company, label, or product line',
    },
    {
        value: 'category',
        title: 'Category',
        description: 'Topic, ingredient, or trend data',
    },
];

// Stable default: the open-effect depends on these arrays, so a fresh [] per render loops forever.
const NO_ITEMS = [];

const GENERATING_STATUS_BADGE_CLASS = 'bg-[#F6E7B2] text-[#9A6700]';

function OverviewBanner({
    children,
    showGeneratingBadge = false,
    tone = 'neutral',
}) {
    return (
        <div
            className={cn(
                'mb-4 flex items-center gap-3 rounded-[24px] border px-5 py-4',
                tone === 'warning'
                    ? 'border-[#F2E4B5] bg-[#FFF8E8]'
                    : 'border-[#E6E7EB] bg-[#F7F7F8]',
            )}
        >
            <Sparkles className="h-4 w-4 shrink-0 text-[#7A7A7A]" />
            <Text
                as="span"
                className="text-[14px] font-medium leading-6 text-[#6B6B6B]"
            >
                {children}
            </Text>
            {showGeneratingBadge ? (
                <span
                    className={cn(
                        'ml-auto rounded-full px-4 py-1.5 text-[14px] font-medium leading-5',
                        GENERATING_STATUS_BADGE_CLASS,
                    )}
                >
                    Generating
                </span>
            ) : null}
        </div>
    );
}

function parseTerritorySections(territoryText) {
    if (!territoryText) return [];
    const lines = territoryText.split('\n');
    const sections = [];
    let currentSection = null;

    lines.forEach((line) => {
        if (line.startsWith('#')) {
            if (currentSection) sections.push(currentSection);
            currentSection = {
                heading: line.replace(/^#+\s*/, ''),
                paragraphs: [],
            };
            return;
        }
        if (!line.trim()) return;
        if (!currentSection) {
            currentSection = { heading: null, paragraphs: [] };
        }
        currentSection.paragraphs.push(line);
    });

    if (currentSection) sections.push(currentSection);
    return sections;
}

function TerritorySections({ sections }) {
    return (
        <div
            className="flex flex-col gap-4 max-w-[760px]"
            style={{
                fontFamily: 'Montserrat, sans-serif',
            }}
        >
            <AiGeneratedLabel tone="strong" />
            {sections.map((section, index) => (
                <div key={index} className="flex flex-col gap-2">
                    {section.heading ? (
                        <Text
                            as="p"
                            weight="medium"
                            size="base"
                            className="text-[14px] leading-5"
                        >
                            {section.heading}
                        </Text>
                    ) : null}
                    {section.paragraphs.map((paragraph, pIndex) => (
                        <Text
                            key={pIndex}
                            as="p"
                            size="sm"
                            className="text-[13px] leading-5"
                        >
                            {paragraph}
                        </Text>
                    ))}
                </div>
            ))}
        </div>
    );
}

export default function ContextModal({
    open,
    onOpenChange,
    contexts = NO_ITEMS,
    selectedContextIds = NO_ITEMS,
    maxSelected = 3,
    onApplySelection,
}) {
    const createContext = useCreateAlertContext();
    const updateContext = useUpdateAlertContext();
    const regenerateContext = useUpdateAlertContext();
    const deleteContext = useDeleteAlertContext();
    const [mode, setMode] = useState('manager'); // manager | create | edit
    const [searchTerm, setSearchTerm] = useState('');
    const [draftSelectedIds, setDraftSelectedIds] = useState([]);
    const [activeContextId, setActiveContextId] = useState(null);
    const [editingContextId, setEditingContextId] = useState(null);
    const [isEditingOverview, setIsEditingOverview] = useState(false);
    const [territoryDraft, setTerritoryDraft] = useState('');
    const [formData, setFormData] = useState(DEFAULT_FORM);

    const activeContext = useMemo(
        () =>
            contexts.find((ctx) => ctx.alert_context_id === activeContextId) ||
            null,
        [contexts, activeContextId],
    );

    const filteredContexts = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        const matchingContexts = q
            ? contexts.filter((ctx) =>
                  (ctx.name || '').toLowerCase().includes(q),
              )
            : contexts;

        return [...matchingContexts].sort((a, b) => {
            const aSelected = draftSelectedIds.includes(a.alert_context_id);
            const bSelected = draftSelectedIds.includes(b.alert_context_id);
            if (aSelected === bSelected) return 0;
            return aSelected ? -1 : 1;
        });
    }, [contexts, searchTerm, draftSelectedIds]);

    useEffect(() => {
        if (!open) {
            setMode('manager');
            setSearchTerm('');
            setDraftSelectedIds([]);
            setActiveContextId(null);
            setEditingContextId(null);
            setIsEditingOverview(false);
            setTerritoryDraft('');
            setFormData(DEFAULT_FORM);
            return;
        }
        setMode('manager');
        setSearchTerm('');
        setDraftSelectedIds(selectedContextIds || []);
        const initialFromSelection = (selectedContextIds || []).find(
            (contextId) =>
                contexts.some((ctx) => ctx.alert_context_id === contextId),
        );
        setActiveContextId(
            initialFromSelection || contexts[0]?.alert_context_id || null,
        );
    }, [open, selectedContextIds, contexts]);

    useEffect(() => {
        if (!open || mode !== 'manager') return;
        if (
            activeContextId &&
            contexts.some((ctx) => ctx.alert_context_id === activeContextId)
        ) {
            return;
        }
        const initialFromSelection = draftSelectedIds.find((contextId) =>
            contexts.some((ctx) => ctx.alert_context_id === contextId),
        );
        setActiveContextId(
            initialFromSelection || contexts[0]?.alert_context_id || null,
        );
    }, [open, mode, activeContextId, contexts, draftSelectedIds]);

    useEffect(() => {
        if (!open || mode !== 'manager') return;
        if (!filteredContexts.length) {
            setActiveContextId(null);
            return;
        }
        if (
            activeContextId &&
            filteredContexts.some(
                (ctx) => ctx.alert_context_id === activeContextId,
            )
        ) {
            return;
        }
        setActiveContextId(filteredContexts[0].alert_context_id);
    }, [open, mode, activeContextId, filteredContexts]);

    const handleToggleSelected = (contextId) => {
        setDraftSelectedIds((prev) => {
            if (prev.includes(contextId)) {
                return prev.filter((id) => id !== contextId);
            }
            if (prev.length >= maxSelected) {
                toast.error(`You can select up to ${maxSelected} contexts.`);
                return prev;
            }
            return [...prev, contextId];
        });
    };

    const handleApply = () => {
        onApplySelection?.(draftSelectedIds);
        onOpenChange(false);
    };

    const openCreate = () => {
        setEditingContextId(null);
        setFormData(DEFAULT_FORM);
        setMode('create');
    };

    const openEdit = (contextId) => {
        const context = contexts.find(
            (ctx) => ctx.alert_context_id === contextId,
        );
        if (!context) return;
        setEditingContextId(contextId);
        setActiveContextId(contextId);
        setFormData({
            name: context.name || '',
            context_type: context.context_type || 'brand',
            website_url: context.website_url || '',
            instructions: context.instructions || '',
        });
        setTerritoryDraft(context.territory_text || '');
        setIsEditingOverview(false);
        setMode('edit');
    };

    const handleBackToManager = () => {
        setMode('manager');
        setEditingContextId(null);
        setIsEditingOverview(false);
        setTerritoryDraft('');
        setFormData(DEFAULT_FORM);
    };

    const handleSaveContext = async () => {
        if (!formData.name.trim()) {
            toast.error('Context name is required');
            return;
        }
        if (formData.context_type === 'brand' && !formData.website_url.trim()) {
            toast.error('Website URL is required for brand context');
            return;
        }
        if (
            formData.context_type === 'category' &&
            !formData.instructions.trim()
        ) {
            toast.error('Instructions are required for category context');
            return;
        }

        try {
            const payload = {
                name: formData.name.trim(),
                context_type: formData.context_type,
                website_url:
                    formData.context_type === 'brand'
                        ? formData.website_url.trim()
                        : null,
                instructions:
                    formData.context_type === 'category'
                        ? formData.instructions.trim()
                        : null,
            };
            if (mode === 'edit' && isEditingOverview) {
                if (!territoryDraft.trim()) {
                    toast.error('Generated context must not be empty');
                    return;
                }
                payload.territory_text = territoryDraft.trim();
            }

            const context = editingContextId
                ? await updateContext.mutateAsync({
                      alertContextId: editingContextId,
                      data: payload,
                  })
                : await createContext.mutateAsync(payload);

            const contextId = context?.alert_context_id;
            if (contextId) {
                setActiveContextId(contextId);
                setDraftSelectedIds((prev) => {
                    if (prev.includes(contextId)) return prev;
                    if (prev.length >= maxSelected) return prev;
                    return [...prev, contextId];
                });
            }

            toast.success(
                editingContextId
                    ? context?.task_id
                        ? 'Context updated. Regenerating...'
                        : 'Context updated.'
                    : 'Context created. Generating territory...',
            );
            handleBackToManager();
        } catch (error) {
            console.error('Failed to save context:', error);
            toast.error('Failed to save context');
        }
    };

    const handleDelete = async () => {
        if (!editingContextId) return;
        try {
            await deleteContext.mutateAsync(editingContextId);
            toast.success('Context deleted.');
            setDraftSelectedIds((prev) =>
                prev.filter((id) => id !== editingContextId),
            );
            handleBackToManager();
        } catch (error) {
            console.error('Failed to delete context:', error);
            toast.error('Failed to delete context');
        }
    };

    const handleRegenerate = async (contextId) => {
        if (!contextId) return;
        try {
            await regenerateContext.mutateAsync({
                alertContextId: contextId,
                data: { regenerate: true },
            });
            toast.success('Context regeneration started.');
        } catch (error) {
            console.error('Failed to regenerate context:', error);
            toast.error('Failed to regenerate context');
        }
    };

    const activeSections = useMemo(
        () => parseTerritorySections(activeContext?.territory_text),
        [activeContext?.territory_text],
    );

    const editingContext = useMemo(
        () =>
            contexts.find((ctx) => ctx.alert_context_id === editingContextId) ||
            null,
        [contexts, editingContextId],
    );
    const editingSections = useMemo(
        () => parseTerritorySections(editingContext?.territory_text),
        [editingContext?.territory_text],
    );
    useEffect(() => {
        if (mode !== 'edit') return;
        setTerritoryDraft(editingContext?.territory_text || '');
    }, [editingContext?.territory_text, mode]);
    const activeContextGenerating = isContextGenerating(activeContext);
    const activeContextPending = isContextPending(activeContext);
    const activeContextHasGeneratedText = Boolean(
        activeContext?.territory_text?.trim(),
    );
    const editingContextGenerating = isContextGenerating(editingContext);
    const editingContextPending = isContextPending(editingContext);
    const editingContextHasGeneratedText = Boolean(
        editingContext?.territory_text?.trim(),
    );

    const isSaving = createContext.isLoading || updateContext.isLoading;
    const splitGridClass =
        'grid grid-cols-1 md:grid-cols-[400px_minmax(0,1fr)] xl:grid-cols-[480px_minmax(0,1fr)]';
    const leftPaneBgClass = 'bg-white';
    const rightPaneBgClass = 'bg-[#F5F5F5]';
    const headerCellClass = 'min-h-[80px]';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="grid-rows-[auto_minmax(0,1fr)_auto] h-[86vh] w-[94vw] max-w-[94vw] gap-0 overflow-hidden p-0 font-sans md:w-[90vw] md:max-w-[90vw] xl:w-[86vw] xl:max-w-[86vw] sm:rounded-[24px] [&>button]:hidden">
                {mode === 'manager' ? (
                    <div
                        className={cn(
                            splitGridClass,
                            'border-b border-[#E6E7EB]',
                        )}
                    >
                        <div
                            className={cn(
                                headerCellClass,
                                leftPaneBgClass,
                                'flex items-center justify-between gap-4 px-6 py-2.5 md:border-r md:border-[#E6E7EB]',
                            )}
                        >
                            <Text
                                as="span"
                                className="text-base font-semibold leading-6 text-[#1A1A1A]"
                            >
                                Select Context
                            </Text>
                            <Button
                                type="button"
                                variant="outline"
                                className="h-10 rounded-md px-4 text-sm font-medium"
                                onClick={openCreate}
                            >
                                <CirclePlus className="mr-2 h-4 w-4" />
                                Create new
                            </Button>
                        </div>
                        <div
                            className={cn(
                                headerCellClass,
                                rightPaneBgClass,
                                'flex items-center justify-between gap-4 px-8 py-2.5',
                            )}
                        >
                            <Text
                                as="span"
                                className="text-base font-semibold leading-6 text-[#1A1A1A]"
                            >
                                Context overview
                            </Text>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onOpenChange(false)}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div
                        className={cn(
                            splitGridClass,
                            'border-b border-[#E6E7EB]',
                        )}
                    >
                        <div
                            className={cn(
                                headerCellClass,
                                leftPaneBgClass,
                                'flex items-center gap-3 px-6 py-2.5 md:border-r md:border-[#E6E7EB]',
                            )}
                        >
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 shrink-0 rounded-md"
                                onClick={handleBackToManager}
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <Text
                                as="span"
                                className="text-base font-semibold leading-6 text-[#1A1A1A]"
                            >
                                {mode === 'edit'
                                    ? 'Edit context'
                                    : 'Create new context'}
                            </Text>
                        </div>
                        <div
                            className={cn(
                                headerCellClass,
                                rightPaneBgClass,
                                'flex items-center justify-between gap-4 px-8 py-2.5',
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <Text
                                    as="span"
                                    className="text-base font-semibold leading-6 text-[#1A1A1A]"
                                >
                                    Context overview
                                </Text>
                                {mode === 'edit' ? (
                                    <>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="h-8 rounded-md px-4 text-sm font-medium"
                                            onClick={() =>
                                                handleRegenerate(
                                                    editingContextId,
                                                )
                                            }
                                            disabled={
                                                !editingContextId ||
                                                editingContextGenerating ||
                                                regenerateContext.isLoading
                                            }
                                        >
                                            Regenerate
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="h-8 rounded-md px-4 text-sm font-medium"
                                            onClick={() =>
                                                setIsEditingOverview(
                                                    (prev) => !prev,
                                                )
                                            }
                                            disabled={editingContextGenerating}
                                        >
                                            {isEditingOverview
                                                ? 'Cancel edit'
                                                : 'Edit'}
                                        </Button>
                                    </>
                                ) : null}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onOpenChange(false)}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                )}

                <div className={cn(splitGridClass, 'min-h-0')}>
                    <div
                        className={cn(
                            leftPaneBgClass,
                            'min-h-0 overflow-y-auto border-b border-[#E6E7EB] px-6 py-5 md:border-b-0 md:border-r md:border-[#E6E7EB]',
                        )}
                    >
                        {mode === 'manager' ? (
                            <div className="flex flex-col gap-4">
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={searchTerm}
                                        onChange={(event) =>
                                            setSearchTerm(event.target.value)
                                        }
                                        placeholder="Search"
                                        className="h-11 rounded-[14px] pl-11 text-sm"
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    {filteredContexts.map((context) => {
                                        const isSelected =
                                            draftSelectedIds.includes(
                                                context.alert_context_id,
                                            );
                                        const isActive =
                                            context.alert_context_id ===
                                            activeContextId;
                                        const blockedByLimit =
                                            !isSelected &&
                                            draftSelectedIds.length >=
                                                maxSelected;
                                        return (
                                            <ContextManagerCard
                                                key={context.alert_context_id}
                                                context={context}
                                                isActive={isActive}
                                                isSelected={isSelected}
                                                blockedByLimit={blockedByLimit}
                                                onActivate={() =>
                                                    setActiveContextId(
                                                        context.alert_context_id,
                                                    )
                                                }
                                                onToggleSelected={() =>
                                                    handleToggleSelected(
                                                        context.alert_context_id,
                                                    )
                                                }
                                                onEdit={() =>
                                                    openEdit(
                                                        context.alert_context_id,
                                                    )
                                                }
                                            />
                                        );
                                    })}
                                    {!filteredContexts.length ? (
                                        <Text as="span" size="sm" color="muted">
                                            No contexts found.
                                        </Text>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {mode === 'create' ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        {TYPE_OPTIONS.map((option) => {
                                            const selected =
                                                formData.context_type ===
                                                option.value;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={cn(
                                                        'rounded-md border p-3 text-left transition-colors',
                                                        selected
                                                            ? 'border-violet-400 bg-violet-50'
                                                            : 'border-input bg-background hover:bg-muted/40',
                                                    )}
                                                    onClick={() => {
                                                        if (
                                                            option.value ===
                                                            'brand'
                                                        ) {
                                                            setFormData(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    context_type:
                                                                        'brand',
                                                                    instructions:
                                                                        '',
                                                                }),
                                                            );
                                                            return;
                                                        }
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            context_type:
                                                                'category',
                                                            website_url: '',
                                                        }));
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <Text
                                                            as="span"
                                                            className="font-medium"
                                                        >
                                                            {option.title}
                                                        </Text>
                                                        {selected ? (
                                                            <Check className="h-4 w-4 text-violet-600" />
                                                        ) : null}
                                                    </div>
                                                    <Text
                                                        as="p"
                                                        size="sm"
                                                        color="muted"
                                                        className="mt-1"
                                                    >
                                                        {option.description}
                                                    </Text>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : null}

                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="context-name">
                                        Context name
                                    </Label>
                                    <Input
                                        id="context-name"
                                        value={formData.name}
                                        onChange={(event) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                name: event.target.value,
                                            }))
                                        }
                                        placeholder="e.g. CeraVe, Retinol Alternatives, K-Beauty..."
                                    />
                                </div>

                                {formData.context_type === 'brand' ? (
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="context-url">
                                            Website URL
                                        </Label>
                                        <Input
                                            id="context-url"
                                            value={formData.website_url}
                                            onChange={(event) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    website_url:
                                                        event.target.value,
                                                }))
                                            }
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="context-instructions">
                                            What are you trying to do?
                                        </Label>
                                        <Textarea
                                            id="context-instructions"
                                            value={formData.instructions}
                                            onChange={(event) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    instructions:
                                                        event.target.value,
                                                }))
                                            }
                                            className="min-h-[160px]"
                                            placeholder={
                                                CATEGORY_INSTRUCTIONS_PLACEHOLDER
                                            }
                                        />
                                        {!formData.instructions.trim() ? (
                                            <div className="flex flex-col gap-2">
                                                <Text
                                                    as="span"
                                                    size="sm"
                                                    color="muted"
                                                >
                                                    Try an example to get
                                                    started:
                                                </Text>
                                                <div className="flex flex-wrap gap-2">
                                                    {CATEGORY_INSTRUCTION_EXAMPLES.map(
                                                        (example) => (
                                                            <Button
                                                                key={
                                                                    example.label
                                                                }
                                                                type="button"
                                                                variant="outline"
                                                                className="h-9 rounded-md px-3 text-sm"
                                                                onClick={() =>
                                                                    setFormData(
                                                                        (
                                                                            prev,
                                                                        ) => ({
                                                                            ...prev,
                                                                            instructions:
                                                                                example.text,
                                                                        }),
                                                                    )
                                                                }
                                                            >
                                                                {example.label}
                                                            </Button>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div
                        className={cn(
                            rightPaneBgClass,
                            'min-h-0 overflow-y-auto px-8 py-6',
                        )}
                    >
                        {mode === 'manager' ? (
                            <>
                                {activeContext ? (
                                    <>
                                        {activeContextGenerating ? (
                                            <OverviewBanner showGeneratingBadge>
                                                AI is generating context. You
                                                can close this window
                                            </OverviewBanner>
                                        ) : activeContextPending ? (
                                            <OverviewBanner tone="warning">
                                                This context needs to be
                                                regenerated.
                                            </OverviewBanner>
                                        ) : null}

                                        {activeContextGenerating &&
                                        !activeContextHasGeneratedText ? (
                                            <ContextOverviewSkeleton />
                                        ) : activeContextGenerating ? null : activeContextPending &&
                                          !activeSections.length ? (
                                            <div className="flex flex-col gap-3">
                                                <Text
                                                    as="span"
                                                    size="sm"
                                                    color="muted"
                                                >
                                                    Regenerate this context to
                                                    generate its overview.
                                                </Text>
                                                <div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="h-9 rounded-md px-4 text-sm font-medium"
                                                        onClick={() =>
                                                            handleRegenerate(
                                                                activeContext?.alert_context_id,
                                                            )
                                                        }
                                                        disabled={
                                                            !activeContext?.alert_context_id ||
                                                            regenerateContext.isLoading
                                                        }
                                                    >
                                                        Regenerate context
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : activeSections.length ? (
                                            <TerritorySections
                                                sections={activeSections}
                                            />
                                        ) : (
                                            <Text
                                                as="span"
                                                size="sm"
                                                color="muted"
                                            >
                                                No generated context yet.
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    <Text as="span" size="sm" color="muted">
                                        Select a context to view details.
                                    </Text>
                                )}
                            </>
                        ) : (
                            <>
                                {mode === 'create' && isSaving ? (
                                    <OverviewBanner showGeneratingBadge>
                                        AI is generating context. You can close
                                        this window
                                    </OverviewBanner>
                                ) : editingContextGenerating ? (
                                    <OverviewBanner showGeneratingBadge>
                                        AI is generating context. You can close
                                        this window
                                    </OverviewBanner>
                                ) : editingContextPending ? (
                                    <OverviewBanner tone="warning">
                                        This context needs to be regenerated.
                                    </OverviewBanner>
                                ) : null}

                                {mode === 'create' && isSaving ? (
                                    <ContextOverviewSkeleton />
                                ) : editingContextGenerating &&
                                  !editingContextHasGeneratedText ? (
                                    <ContextOverviewSkeleton />
                                ) : mode === 'edit' && isEditingOverview ? (
                                    <div className="flex max-w-[760px] flex-col gap-2">
                                        <AiGeneratedLabel tone="strong" />
                                        <Textarea
                                            value={territoryDraft}
                                            onChange={(event) =>
                                                setTerritoryDraft(
                                                    event.target.value,
                                                )
                                            }
                                            className="min-h-[420px] resize-none bg-white"
                                            placeholder="Edit the generated context text."
                                        />
                                    </div>
                                ) : editingContextGenerating ? null : editingSections.length ? (
                                    <TerritorySections
                                        sections={editingSections}
                                    />
                                ) : editingContextPending ? (
                                    <div className="flex flex-col gap-3">
                                        <Text as="span" size="sm" color="muted">
                                            Regenerate this context to generate
                                            its overview.
                                        </Text>
                                        <div>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="h-9 rounded-md px-4 text-sm font-medium"
                                                onClick={() =>
                                                    handleRegenerate(
                                                        editingContextId,
                                                    )
                                                }
                                                disabled={
                                                    !editingContextId ||
                                                    regenerateContext.isLoading
                                                }
                                            >
                                                Regenerate context
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <Text as="span" size="sm" color="muted">
                                        Save to generate and preview context
                                        details.
                                    </Text>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <DialogFooter className="w-full shrink-0 border-t border-[#E6E7EB] px-6 py-5 flex-row items-center justify-between gap-3 sm:justify-between sm:space-x-0">
                    <div>
                        {/* {mode === 'edit' ? (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-rose-600 hover:text-rose-600"
                                onClick={handleDelete}
                                disabled={deleteContext.isLoading}
                            >
                                <Trash2 className="mr-1 h-4 w-4" />
                                Delete context
                            </Button>
                        ) : null} */}
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        {mode === 'manager' ? (
                            <Button type="button" onClick={handleApply}>
                                Done
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                onClick={handleSaveContext}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save & Continue'}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
